'use client';

/**
 * Route Royal First-Party Event Tracker
 *
 * Sends behavioural events (page views, property interactions, etc.) to
 * Route Royal's event store via the same-origin proxy
 * `/api/analytics/rr-collect` (which forwards to `/events/collect`).
 *
 * Same-origin posting avoids CORS and the empty-NEXT_PUBLIC_API_URL failure
 * mode that previously POSTed to a non-existent relative `/api/v1/events/collect`.
 *
 * Events are buffered in a global queue (window.__rr_event_queue) so that
 * events dispatched before the tracker mounts are not lost.
 */

import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { getVisitorId } from '@/lib/attribution/visitor-id';
import { useTenantOptional } from '@/contexts/TenantContext';

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 100;

/** Same-origin proxy — preferred path (no CORS, server resolves API URL). */
const COLLECT_PROXY_PATH = '/api/analytics/rr-collect';

interface RREventDetail {
  name: string;
  params?: Record<string, unknown>;
}

interface RREvent {
  event_name: string;
  visitor_id?: string;
  session_id?: string;
  page_url?: string;
  page_title?: string;
  referrer?: string;
  params?: Record<string, unknown>;
  timestamp?: string;
}

declare global {
  interface Window {
    __rr_event_queue?: RREventDetail[];
  }
}

function getEventQueue(): RREventDetail[] {
  if (typeof window === 'undefined') return [];
  if (!window.__rr_event_queue) {
    window.__rr_event_queue = [];
  }
  return window.__rr_event_queue;
}

function getSessionId(): string {
  if (typeof sessionStorage === 'undefined') return '';
  let sid = sessionStorage.getItem('rr_session_id');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('rr_session_id', sid);
  }
  return sid;
}

/**
 * Returns the visitor ID from the rr_vid / _rr_id cookie, falling back to a
 * session-scoped anonymous ID when the cookie hasn't been set yet.
 */
function getOrCreateVisitorId(): string {
  const cookieVid = getVisitorId();
  if (cookieVid) return cookieVid;

  if (typeof sessionStorage === 'undefined') return crypto.randomUUID();

  let tempVid = sessionStorage.getItem('rr_temp_vid');
  if (!tempVid) {
    tempVid = crypto.randomUUID();
    sessionStorage.setItem('rr_temp_vid', tempVid);
  }
  return tempVid;
}

function logTrackerWarning(message: string, detail?: unknown): void {
  // Always log — silent production failures are why site_events stayed empty.
  if (detail !== undefined) {
    console.warn(`[RR Tracker] ${message}`, detail);
  } else {
    console.warn(`[RR Tracker] ${message}`);
  }
}

export function RouteRoyalTracker() {
  const tenant = useTenantOptional();
  const pathname = usePathname();
  const bufferRef = useRef<RREvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPathRef = useRef<string>('');
  const flushInFlightRef = useRef(false);

  const organisationId = tenant?.organisationId;
  const siteId = tenant?.siteId;

  const flush = useCallback(async () => {
    if (flushInFlightRef.current) return;
    if (!bufferRef.current.length) return;

    if (!organisationId) {
      // Keep events buffered until tenant context is available; do not discard.
      return;
    }

    flushInFlightRef.current = true;
    const events = bufferRef.current.splice(0, bufferRef.current.length);

    const payload: Record<string, unknown> = {
      organisation_id: organisationId,
      events,
    };
    if (siteId) {
      payload.site_id = siteId;
    }

    try {
      const res = await fetch(COLLECT_PROXY_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        logTrackerWarning(`flush failed ${res.status}: ${text.slice(0, 300)}`);
        // Re-queue failed events (cap buffer to avoid unbounded growth)
        bufferRef.current = [...events, ...bufferRef.current].slice(0, MAX_BUFFER);
      } else {
        const body = (await res.json().catch(() => null)) as {
          accepted?: number;
          errors?: number;
        } | null;
        if (body && typeof body.errors === 'number' && body.errors > 0) {
          logTrackerWarning(
            `upstream accepted=${body.accepted ?? 0} errors=${body.errors}`,
          );
        }
      }
    } catch (err) {
      logTrackerWarning('flush error', err);
      bufferRef.current = [...events, ...bufferRef.current].slice(0, MAX_BUFFER);
    } finally {
      flushInFlightRef.current = false;
    }
  }, [organisationId, siteId]);

  const track = useCallback(
    (eventName: string, params?: Record<string, unknown>) => {
      if (typeof window === 'undefined') return;

      const visitorId = getOrCreateVisitorId();

      const event: RREvent = {
        event_name: eventName,
        visitor_id: visitorId,
        session_id: getSessionId(),
        page_url: window.location.href,
        page_title: document.title,
        timestamp: new Date().toISOString(),
      };
      if (document.referrer) event.referrer = document.referrer;
      if (params) event.params = params;

      if (bufferRef.current.length >= MAX_BUFFER) {
        bufferRef.current.shift();
      }
      bufferRef.current.push(event);

      if (bufferRef.current.length >= BATCH_SIZE) {
        void flush();
      }
    },
    [flush],
  );

  // Periodic flush
  useEffect(() => {
    timerRef.current = setInterval(() => {
      void flush();
    }, FLUSH_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      void flush();
    };
  }, [flush]);

  // Track page views on route change
  useEffect(() => {
    if (!pathname || pathname === prevPathRef.current) return;
    prevPathRef.current = pathname;

    const timeout = setTimeout(() => {
      track('page_view');
    }, 100);

    return () => clearTimeout(timeout);
  }, [pathname, track]);

  const drainQueue = useCallback(() => {
    const queue = getEventQueue();
    const items = queue.splice(0, queue.length);
    for (const item of items) {
      track(item.name, item.params);
    }
  }, [track]);

  useEffect(() => {
    drainQueue();

    const handler = () => drainQueue();

    window.addEventListener('rr:event', handler);
    return () => window.removeEventListener('rr:event', handler);
  }, [drainQueue]);

  // Flush on page unload
  useEffect(() => {
    const handleUnload = () => {
      void flush();
    };
    window.addEventListener('pagehide', handleUnload);
    return () => window.removeEventListener('pagehide', handleUnload);
  }, [flush]);

  return null;
}

/**
 * Fire a Route Royal tracking event from anywhere in the app.
 * Works alongside GA — use this for events that need to appear
 * in the CRM contact timeline.
 *
 * Events are pushed to a global queue AND dispatched as a CustomEvent.
 * The queue ensures events fired before RouteRoyalTracker mounts are
 * not lost (the tracker drains it on listener attach).
 */
export function fireRREvent(name: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  getEventQueue().push(params ? { name, params } : { name });
  window.dispatchEvent(
    new CustomEvent('rr:event', { detail: { name, params } }),
  );
}
